/** @format */

import {useState, useRef, useLayoutEffect, MouseEvent} from 'react';

import {Popover, useTheme} from '@mui/material';
import StickyNote from '@mui/icons-material/StickyNote2';
import AddIcon from '@mui/icons-material/Add';

import {Root, IconWrapper, Badge} from './styles';
import NoteModal from '../modal';
import AggregatedNoteModal from '../modal/AggregatedNoteModal';
import NotesIcon from './notes-icon.svg?react';
import {ScatterButtonData} from '../../../models/scatters';
import {useNoteOpen} from '../../../contexts/NoteOpenContext';

interface Props {
  buttonData?: ScatterButtonData;
  date?: string;
  refetchNotes?: () => void;
}

const buildKey = (date: string | undefined, buttonData: ScatterButtonData | undefined): string => {
  const startOn = buttonData?.tooltipData.startOn;
  const endOn = buttonData?.tooltipData.endOn;
  if (startOn) {
    return `${startOn}_${endOn ?? ''}`;
  }
  return date ?? endOn ?? '';
};

interface ButtonContentProps {
  notesCount: number;
}

const ButtonContent = ({notesCount}: ButtonContentProps) => {
  if (notesCount === 0) {
    return (
      <IconWrapper>
        <AddIcon />
      </IconWrapper>
    );
  }
  if (notesCount === 1) {
    return (
      <IconWrapper>
        <StickyNote />
      </IconWrapper>
    );
  }
  return (
    <IconWrapper>
      <NotesIcon />
      <Badge>{notesCount}</Badge>
    </IconWrapper>
  );
};

const NoteButton = ({buttonData, date, refetchNotes}: Props) => {
  const {openKey, setOpenKey} = useNoteOpen();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const theme = useTheme();

  const key = buildKey(date, buttonData);
  const shouldBeOpen = openKey === key && key !== '';

  // useLayoutEffect синхронно перед paint восстанавливает anchor после ремаунта.
  // Без него между ремаунтом и async setState успевает кадр с open=true && anchorEl=null,
  // что вызывает закрытие popover и мерцание в UI.
  useLayoutEffect(() => {
    setAnchorEl(shouldBeOpen ? buttonRef.current : null);
  }, [shouldBeOpen]);

  const isOpen = shouldBeOpen && anchorEl !== null;
  const id = isOpen ? 'notes-popover' : undefined;
  const notesCount = buttonData?.value || 0;
  const isAggregated = Boolean(buttonData?.tooltipData.startOn);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpenKey(key);
  };

  const handleClose = () => {
    setOpenKey(null);
  };

  return (
    <>
      <Root
        ref={buttonRef}
        onClick={handleClick}
        notesCount={notesCount}
        isActive={isOpen}
        data-testid='note-button'
      >
        <ButtonContent notesCount={notesCount} />
      </Root>

      <Popover
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        data-testid='note-modal'
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              background: 'none',
              boxShadow: 'none',
              overflow: 'visible',
              marginTop: theme.spacing(1),
            },
          },
        }}
      >
        {isAggregated ? (
          <AggregatedNoteModal tooltipData={buttonData?.tooltipData} refetchNotes={refetchNotes} />
        ) : (
          <NoteModal notesCount={notesCount} date={date} refetchNotes={refetchNotes} />
        )}
      </Popover>
    </>
  );
};

export default NoteButton;
